import Segment from '@/app/users/tracking/segment.js'

import { NUMBER_OF_DAYS_TO_FETCH_ALL_ORDERS, NUMBER_OF_DAYS_TO_FETCH_CUSTOM_DATES } from '@/app/packgo/shared/constants'

/** @typedef {import('@/types/packgo.models').PackGoSettings} PackGoSettings */
/** @typedef {import('@/types/packgo.models').Filters} Filters */
/** @typedef {import('@/types/packgo.models').PackGoItemPacking} PackGoItemPacking */
/** @typedef {import('@/types/packgo.models').PackGoPerformanceMetrics} PackGoPerformanceMetrics */
// eslint-disable-next-line @stylistic/max-len
/** @typedef {import('@/app/packgo/shared/composables/use-item-packing').ItemPackingAnalyticsData} ItemPackingAnalyticsData */

const trackAll = {
  integrations: {
    Mixpanel: true,
    All: true,
  },
}

const dateRangeFilter = (filters) => {
  if (filters?.date_range?.days_ago === NUMBER_OF_DAYS_TO_FETCH_ALL_ORDERS) {
    return 'All time'
  } else if (filters?.date_range?.days_ago !== NUMBER_OF_DAYS_TO_FETCH_CUSTOM_DATES) {
    return `${filters?.date_range?.days_ago} days ago`
  } else {
    return `${filters?.date_range?.start}-${filters?.date_range?.end}`
  }
}

export default {
  /**
   * @param {boolean} isOptingIn
   */
  optInForNewPackGo(isOptingIn) {
    const activeStatus = isOptingIn ? 'in' : 'out'
    const eventName = `New Pack & Go - Opt ${activeStatus}`
    Segment.track(eventName, {}, trackAll)
  },

  /**
   * @param {boolean} isNewPackGo
   */
  openPackGoQueue(isNewPackGo) {
    const eventPrefix = isNewPackGo ? 'New ' : ''
    const eventName = eventPrefix + 'Pack & Go - Open Queue'
    Segment.track(eventName, {}, trackAll)
  },

  openPackGoOrderDetailsPanel() {
    const eventName = 'New Pack & Go - Open order details panel'
    Segment.track(eventName, {}, trackAll)
  },

  /**
   *
   * @param {{
   *   isNewPackGo: boolean
   *   mode: 'automatic' | 'scanner'
   *   scannerMode: 'Single' | 'Double'
   *   isLabelCreated: boolean
   *   userSubscriptionPlan: string
   *   performanceMetrics?: PackGoPerformanceMetrics
   *   itemPackingAnalyticsData?: ItemPackingAnalyticsData
   *   filters: Filters
   * }} data
   */
  packGoCreateLabel({
    isNewPackGo,
    mode,
    scannerMode,
    isLabelCreated,
    userSubscriptionPlan,
    performanceMetrics,
    itemPackingAnalyticsData,
    filters,
  }) {
    const eventPrefix = isNewPackGo ? 'New ' : ''
    Segment.track(
      eventPrefix + 'Pack & Go - Create label',
      {
        'Mode': mode,
        'Scanner Mode': scannerMode,
        'Label created': isLabelCreated,
        'User subscription plan': userSubscriptionPlan,
        'Load order time': performanceMetrics?.loadOrderTime,
        'Time on page (seconds)': performanceMetrics?.timeOnPage,
        'Process order time': performanceMetrics?.processOrderTime,
        'Load till process order time': performanceMetrics?.loadTillProcessOrderTime,
        'Load order till packing all items': performanceMetrics?.loadTillPackingAllItems,
        'Average barcode lookup failure time': performanceMetrics?.averageBarcodeLookupFailureTime,
        'Item scan success': itemPackingAnalyticsData?.itemsInteractions.scanSuccess,
        'Item scan failure': itemPackingAnalyticsData?.itemsInteractions.scanFailure,
        'Item button pack': itemPackingAnalyticsData?.itemsInteractions.buttonPack,
        'Item button unpack': itemPackingAnalyticsData?.itemsInteractions.buttonUnpack,
        'Item menu pack': itemPackingAnalyticsData?.itemsInteractions.menuPack,
        'Item menu pack all': itemPackingAnalyticsData?.itemsInteractions.menuPackAll,
        'Item menu unpack': itemPackingAnalyticsData?.itemsInteractions.menuUnpack,
        'Item menu unpack all': itemPackingAnalyticsData?.itemsInteractions.menuUnpackAll,
        'Items packed': itemPackingAnalyticsData?.itemsPacked,
        'Items count': itemPackingAnalyticsData?.itemsCount,
        'Integration filter': filters?.integration_id || 'All',
        'Date range filter': dateRangeFilter(filters),
        'Carriers filter': filters?.carriers,
      },
      trackAll,
    )
  },

  /**
   * Pack&GO settings changed
   *
   * @param {('Queue' | 'Settings')} source
   * @param {PackGoSettings} settings
   */
  packGoSettings(source, settings) {
    Segment.track(
      'New Pack & Go - Settings',
      {
        'Source': source,
        'mode': settings.mode,
        'single_scan': settings.single_scan,
        'process_duplicate_warning': settings.process_duplicate_warning,
        'reverse_queue_ordering': settings.reverse_queue_ordering,
        'show_orders_with_errors': settings.show_orders_with_errors,
        'hide_item_images': settings.hide_item_images,
        'item_packing.enabled': settings.item_packing?.enabled,
        'item_packing.create_label_mode': settings.item_packing?.create_label_mode,
        'item_packing.create_label_auto': settings.item_packing?.create_label_auto,
      },
      trackAll,
    )
  },

  /**
   * @param {string} source
   */
  openHelpCenter(source) {
    Segment.track(
      'New Pack & Go - Open help center',
      {
        source: source,
      },
      trackAll,
    )
  },
}
